import { auth } from '../firebase.config';

export default class HttpClient {

  private static readonly ROOT_URL = `${import.meta.env.VITE_LEEDPULSE_API_URL}/api/v1`;

  // (A) Always resolve a fresh Firebase ID token at request time. getIdToken()
  // transparently refreshes the token when the cached one is expired or near
  // expiry, so we never send a stale snapshot. The explicitly passed token is
  // only used as a fallback when there is no signed-in user.
  private static async resolveToken(fallback?: string): Promise<string | undefined> {
    try {
      const fresh = await auth.currentUser?.getIdToken();
      return fresh ?? fallback;
    } catch {
      return fallback;
    }
  }

  private static async parseError(response: Response): Promise<string> {
    let errorMessage = `HTTP error! Status: ${response.status}`;
    try {
      const errorData = await response.json();
      if (errorData?.data?.msg) errorMessage = errorData.data.msg;
      else if (errorData?.data?.message) errorMessage = errorData.data.message;
      else if (errorData?.message) errorMessage = errorData.message;
    } catch {
      // If the server does not return valid JSON, we keep the basic message
    }
    return errorMessage;
  }

  // Core request: (A) fresh token on send, (B) on 401 force a token refresh and
  // retry once. Returns the raw Response so callers can read json() or blob().
  private static async execute(
    endpoint: string,
    init: Omit<RequestInit, 'headers'>,
    bearerToken?: string,
    additionalHeaders: Record<string, string> = {},
    contentTypeJson = true,
  ): Promise<Response> {
    const url = `${HttpClient.ROOT_URL}${endpoint}`;

    const buildHeaders = (token?: string): Record<string, string> => ({
      ...(contentTypeJson ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${token}`,
      ...additionalHeaders,
    });

    let token = await HttpClient.resolveToken(bearerToken);
    let response = await fetch(url, { ...init, headers: buildHeaders(token) });

    // (B) The token may have expired between resolution and server-side
    // verification. Force a refresh and replay the request once.
    if (response.status === 401 && auth.currentUser) {
      try {
        token = await auth.currentUser.getIdToken(true);
        response = await fetch(url, { ...init, headers: buildHeaders(token) });
      } catch {
        // fall through to the error handling below
      }
    }

    if (!response.ok) {
      throw new Error(await HttpClient.parseError(response));
    }

    return response;
  }

  // get
  public static async get(
    endpoint: string,
    bearerToken?: string,
    additionalHeaders = {}
  ): Promise<any> {
    const response = await HttpClient.execute(endpoint, { method: 'GET' }, bearerToken, additionalHeaders);
    return response.json();
  }

  // GET request that returns a Blob (for file downloads)
  public static async getBlob(
    endpoint: string,
    bearerToken?: string,
    additionalHeaders = {}
  ): Promise<Blob> {
    const response = await HttpClient.execute(endpoint, { method: 'GET' }, bearerToken, additionalHeaders);
    return response.blob();
  }

  public static async post(
    endpoint: string,
    payload: any,
    bearerToken: string,
    isFormData = false,
    additionalHeaders = {}
  ): Promise<any> {
    const response = await HttpClient.execute(
      endpoint,
      { method: 'POST', body: isFormData ? (payload as FormData) : JSON.stringify(payload) },
      bearerToken,
      additionalHeaders,
      !isFormData,
    );
    return response.json();
  }

  // Delete method
  public static async delete(
    endpoint: string,
    payload: any,
    bearerToken: string,
    additionalHeaders = {}
  ): Promise<any> {
    const response = await HttpClient.execute(
      endpoint,
      { method: 'DELETE', body: JSON.stringify(payload) },
      bearerToken,
      additionalHeaders,
    );
    return response.json();
  }

  // PUT method
  public static async put(
    endpoint: string,
    payload: any,
    bearerToken: string,
    additionalHeaders = {}
  ): Promise<any> {
    const response = await HttpClient.execute(
      endpoint,
      { method: 'PUT', body: JSON.stringify(payload) },
      bearerToken,
      additionalHeaders,
    );
    return response.json();
  }

  // PATCH method
  public static async patch(
    endpoint: string,
    payload: any,
    bearerToken: string,
    additionalHeaders = {}
  ): Promise<any> {
    const response = await HttpClient.execute(
      endpoint,
      { method: 'PATCH', body: JSON.stringify(payload) },
      bearerToken,
      additionalHeaders,
    );
    return response.json();
  }

  /**
   * POST request that returns a Blob (for file downloads)
   */
  public static async postBlob(
    endpoint: string,
    payload: any,
    bearerToken: string,
    additionalHeaders = {}
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await HttpClient.execute(
      endpoint,
      { method: 'POST', body: JSON.stringify(payload) },
      bearerToken,
      additionalHeaders,
    );

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'download'; // default name
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    return { blob, filename };
  }
}
